import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useState } from 'react';
import { priorityColor } from '../../shared/constants';
import { Card } from '../../shared/components';
import useAsyncData from '../../shared/hooks/useAsyncData';
import { currentIsoWeek, daysOfWeek, shiftWeek } from '../domain/isoWeek';
import {
  WeekAssignments,
  WeekTask,
  WeekVisit,
  getWeekAssignments,
} from '../services/assignment.api';

/**
 * A week of assigned work, across every job.
 *
 * <b>Why tasks are not on days.</b> A site visit carries an `assigned_date`, so it belongs
 * to a particular day and sits in that day's column. A task carries only `assigned_week` —
 * the database has no day for it — so tasks go in a lane above the grid, belonging to the
 * week rather than to any day in it. Spreading them across days would mean inventing a
 * date the office never chose.
 *
 * Read-only on purpose: "manage" here is seeing what is assigned and opening the job it
 * belongs to. Moving a task between days is not possible until tasks carry a date, and
 * moving one between weeks is a change to the assignment that belongs on the assign form.
 */

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Tasks belong to a week, and the week's working days are what they occupy. */
const WORKING_DAYS = 5;

function formatDay(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

const SHIFT_LABELS: Record<string, string> = {
  first_half: 'First half',
  second_half: 'Second half',
  full_day: 'Full day',
};

export default function AssignedWorkCalendar() {
  const [{ week, year }, setWeek] = useState(currentIsoWeek);

  const fetchWeek = useCallback(() => getWeekAssignments(week, year), [week, year]);
  const { data, loading, error, refetch } = useAsyncData<WeekAssignments>(
    fetchWeek,
    { week, year, week_start: '', week_end: '', tasks: [], visits: [] },
    {
      code: 'WEEK_ASSIGNMENTS_FAILED',
      errorMessage: 'Failed to load the week’s assignments',
      toastOnError: false,
    }
  );

  /** Visits keyed by their ISO date, so each column is one lookup rather than a scan. */
  const visitsByDay = useMemo(() => {
    const map = new Map<string, WeekVisit[]>();
    for (const visit of data.visits ?? []) {
      const day = visit.assigned_date?.slice(0, 10);
      if (!day) continue;
      const bucket = map.get(day);
      if (bucket) bucket.push(visit);
      else map.set(day, [visit]);
    }
    return map;
  }, [data.visits]);

  const days = useMemo(() => daysOfWeek(data.week_start), [data.week_start]);
  const today = new Date().toISOString().slice(0, 10);
  const isCurrentWeek = useMemo(() => {
    const now = currentIsoWeek();
    return now.week === week && now.year === year;
  }, [week, year]);

  /**
   * Whether the week carries tasks at all.
   *
   * A task has no day, only a week, so the week's working days are what it occupies —
   * that is what marks Mon-Fri as assigned below. Saturday and Sunday are left alone: the
   * office does not assign into them, and shading them would overstate the load.
   */
  const weekHasTasks = (data.tasks ?? []).length > 0;

  return (
    <Card
      title='Assigned Work Calendar'
      action={
        <Stack direction='row' alignItems='center' gap={0.5}>
          <Tooltip title='Previous week'>
            <IconButton size='small' onClick={() => setWeek(shiftWeek({ week, year }, -1))}>
              <Icon icon='mdi:chevron-left' width={22} />
            </IconButton>
          </Tooltip>

          <Typography variant='body2' sx={{ minWidth: 190, textAlign: 'center' }}>
            {data.week_start
              ? `Week ${data.week} · ${formatDay(data.week_start)} – ${formatDay(data.week_end)}`
              : `Week ${week}, ${year}`}
          </Typography>

          <Tooltip title='Next week'>
            <IconButton size='small' onClick={() => setWeek(shiftWeek({ week, year }, 1))}>
              <Icon icon='mdi:chevron-right' width={22} />
            </IconButton>
          </Tooltip>

          <Tooltip title='This week'>
            {/* Spans the button so the tooltip still shows while it is disabled. */}
            <Box component='span'>
              <IconButton
                size='small'
                disabled={isCurrentWeek}
                onClick={() => setWeek(currentIsoWeek())}
              >
                <Icon icon='mdi:calendar-today' width={20} />
              </IconButton>
            </Box>
          </Tooltip>

          <Tooltip title='Refresh'>
            <IconButton size='small' onClick={() => refetch()}>
              <Icon icon='mdi:refresh' width={20} />
            </IconButton>
          </Tooltip>
        </Stack>
      }
    >
      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack alignItems='center' py={4}>
          <CircularProgress size={26} />
        </Stack>
      ) : (
        <Box className='column gap-3'>
          <WeekTaskLane tasks={data.tasks ?? []} />

          <Box sx={{ overflowX: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(150px, 1fr))',
                gap: 1,
                minWidth: 1050,
              }}
            >
              {days.map((day, index) => (
                <DayColumn
                  key={day}
                  name={DAY_NAMES[index]}
                  date={day}
                  isToday={day === today}
                  visits={visitsByDay.get(day) ?? []}
                  // Assigned means there is work on this day: a visit of its own, or the
                  // week's tasks, which occupy its working days.
                  isAssigned={
                    (visitsByDay.get(day) ?? []).length > 0 ||
                    (weekHasTasks && index < WORKING_DAYS)
                  }
                />
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Card>
  );
}

/**
 * The week's tasks, above the day grid.
 *
 * Headed with why they are not in the columns — otherwise the lane reads as a bug rather
 * than as what the data actually says.
 */
function WeekTaskLane({ tasks }: { tasks: WeekTask[] }) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        bgcolor: 'action.hover',
      }}
    >
      <Stack direction='row' alignItems='baseline' gap={1} mb={tasks.length ? 1 : 0}>
        <Typography variant='body2' color='text.primary'>
          Tasks this week
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {tasks.length === 0
            ? 'None assigned'
            : `${tasks.length} assigned · scheduled by week, not by day`}
        </Typography>
      </Stack>

      {tasks.length > 0 && (
        <Stack direction='row' flexWrap='wrap' gap={1}>
          {tasks.map((task) => (
            <TaskChip key={task.id} task={task} />
          ))}
        </Stack>
      )}
    </Box>
  );
}

function TaskChip({ task }: { task: WeekTask }) {
  const job = task.job_code ?? (task.job_id ? `Job #${task.job_id}` : 'Unassigned job');
  const detail = [
    task.job_name,
    task.employee_name ? `Assigned to ${task.employee_name}` : 'Unassigned',
    task.alloted_time ? `Allotted ${task.alloted_time}` : null,
    task.status,
    task.note,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Tooltip title={detail}>
      <Chip
        size='small'
        variant='outlined'
        label={`${job} — ${task.task_name ?? 'Task'}`}
        sx={{
          maxWidth: 320,
          borderColor: task.priority ? priorityColor(task.priority) : undefined,
        }}
      />
    </Tooltip>
  );
}

/**
 * One day of the week.
 *
 * `isAssigned` shades the column so the week's load reads at a glance — which is the point
 * of the calendar. A day counts as assigned when it has a site visit of its own, or when
 * the week has tasks and this is one of its working days.
 */
function DayColumn({
  name,
  date,
  isToday,
  isAssigned,
  visits,
}: {
  name: string;
  date: string;
  isToday: boolean;
  isAssigned: boolean;
  visits: WeekVisit[];
}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: isToday ? 'primary.main' : 'divider',
        borderRadius: 1,
        minHeight: 160,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: isAssigned && !isToday ? 'action.hover' : 'transparent',
      }}
    >
      <Stack
        direction='row'
        alignItems='baseline'
        justifyContent='space-between'
        sx={{
          px: 1,
          py: 0.75,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: isToday ? 'primary.main' : 'transparent',
          color: isToday ? 'primary.contrastText' : 'text.primary',
        }}
      >
        <Typography variant='body2'>{name}</Typography>
        <Stack direction='row' alignItems='center' gap={0.5}>
          {isAssigned && (
            <Tooltip title='Work assigned'>
              <Box component='span' sx={{ display: 'flex' }}>
                <Icon icon='mdi:circle-medium' width={16} />
              </Box>
            </Tooltip>
          )}
          <Typography variant='caption'>{formatDay(date)}</Typography>
        </Stack>
      </Stack>

      <Box className='column gap-1' sx={{ p: 1, flex: 1 }}>
        {visits.length === 0 ? (
          <Typography variant='caption' color='text.disabled'>
            {isAssigned ? 'Tasks only' : 'Free'}
          </Typography>
        ) : (
          visits.map((visit) => <VisitCard key={visit.id} visit={visit} />)
        )}
      </Box>
    </Box>
  );
}

function VisitCard({ visit }: { visit: WeekVisit }) {
  const shift = visit.assigned_shift ? SHIFT_LABELS[visit.assigned_shift] ?? visit.assigned_shift : null;

  return (
    <Tooltip title={[visit.job_name, visit.site_address, visit.note].filter(Boolean).join(' · ')}>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderLeft: 3,
          borderLeftColor: visit.director_visit ? 'warning.main' : 'info.main',
          borderRadius: 0.5,
          p: 0.75,
        }}
      >
        <Typography variant='caption' color='text.primary' display='block' noWrap>
          {visit.job_code ?? `Job #${visit.job_id ?? '—'}`}
        </Typography>
        <Typography variant='caption' color='text.secondary' display='block' noWrap>
          {visit.employee_name ?? 'Unassigned'}
        </Typography>
        {shift && (
          <Typography variant='caption' color='text.secondary' display='block' noWrap>
            {shift}
            {visit.director_visit ? ' · Director' : ''}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}
